import { useState } from "react";
import { useAppSelector } from "api/hooks/apiHook";
import { IBillingCx } from "api/types/billing";
import { IBillingCxError } from "api/types/billing";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { validateEmail } from "functions/value";
import { validatePhoneNumber } from "functions/value";
import { validatePostalCode } from "functions/value";
import { text_primary } from "styles/style";
import { grey_color } from "styles/style";

interface Props {
  submitBilling: (customer: IBillingCx) => void
}

export default function Billing({ submitBilling }: Props) {
  const { user } = useAppSelector((state) => state.authentication);
  const { loading } = useAppSelector((state) => state.authentication);
  const [customer, setCustomer] = useState<IBillingCx>({
    address: {
      country: "CA",
      state: "AB",
    },
    tax: {
      ip_address: "68.148.28.46"
    }
  });

  const [customer_error, setCustomerError] = useState<IBillingCxError>({});

  const useAccountDetails = () => {
    setCustomer({
      email: user.email ? user.email : "",
      name: `${user.first_name} ${user.last_name}`,
      phone: user.phone_number ? user.phone_number : "",
      address: {
        city: user.city ? user.city : "",
        postal_code: user.postal_code ? user.postal_code : "",
        line1: user.address ? user.address : "",
        line2: "",
        state: "AB",
        country: "CA"
      },
      tax: {
        ip_address: "68.148.28.46"
      }
    });
  }

  const changeCustomer = (key: string, value: string) => {
    setCustomer({ ...customer, [key]: value });
    setCustomerError({ ...customer_error, [`${key}_error`]: "", [`${key}_has_error`]: false });
  }

  const changeAddress = (key: string, value: string) => {
    setCustomer({ ...customer, address: { ...customer.address, [key]: value } });
    setCustomerError({ ...customer_error, [`${key}_error`]: "", [`${key}_has_error`]: false });
  }

  const handleSubmitBilling = () => {
    let error: boolean = false;
    const new_errors: IBillingCxError = {};

    if (!validateEmail(customer.email)) {
      new_errors.email_error = "Must enter a valid email";
      new_errors.email_has_error = true;
      error = true;
    }

    if (customer.name === "") {
      new_errors.name_error = "Name must not be blank";
      new_errors.name_has_error = true;
      error = true;
    }

    if (!validatePhoneNumber(customer.phone)) {
      new_errors.phone_error = "Must enter a valid phone number";
      new_errors.phone_has_error = true;
      error = true;
    }

    if (customer.address.city === "") {
      new_errors.city_error = "Must enter a city";
      new_errors.city_has_error = true;
      error = true;
    }

    if (!validatePostalCode(customer.address.postal_code)) {
      new_errors.postal_code_error = "Must enter a valid postal code";
      new_errors.postal_code_has_error = true;
      error = true;
    }

    if (customer.address.line1 === "" && customer.address.line2 === "") {
      new_errors.address_error = "Must enter an address";
      new_errors.address_has_error = true;
      error = true;
    }

    if (error) {
      setCustomerError(new_errors);
      return;
    }

    submitBilling(customer);
  }

  return (
    <Grid container item alignItems="center" sx={{ width: "100%", paddingLeft: 3.5, paddingRight: 3.5 }}>
      <Grid item xs={6}>
        <Typography variant="h6" sx={{ color: text_primary }}>
          Billing Details
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Box display="flex" justifyContent="flex-end">
          <Button size="small" onClick={useAccountDetails}>
            Use Account Details
          </Button>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2" sx={{ color: grey_color, paddingLeft: 0.5 }}>
          Name
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <TextField
          fullWidth
          label="Full Name"
          onChange={(e) => changeCustomer("name", e.target.value)}
          error={customer_error?.name_has_error}
          helperText={customer_error?.name_error ?? ""}
          value={customer.name}
          size="small" />
      </Grid>
      <Grid item xs={9}></Grid>
      <Grid item xs={12}>
        <Typography variant="body2" sx={{ color: grey_color, paddingLeft: 0.5, paddingTop: 1 }}>
          Address
        </Typography>
      </Grid>
      <Grid item xs={3} sx={{ paddingRight: 1, marginBottom: 1 }}>
        <TextField
          fullWidth
          label="Address 1"
          onChange={(e) => changeAddress("line1", e.target.value)}
          error={customer_error?.address_has_error}
          helperText={customer_error?.address_error ?? ""}
          value={customer.address.line1}
          size="small" />
      </Grid>
      <Grid item xs={3} sx={{ paddingRight: 1, marginBottom: 1 }}>
        <TextField
          fullWidth
          label="Address 2"
          onChange={(e) => changeAddress("line2", e.target.value)}
          error={customer_error?.address_has_error}
          helperText={customer_error?.address_error ?? ""}
          value={customer.address.line2}
          size="small" />
      </Grid>
      <Grid item xs={3} sx={{ paddingRight: 1 }}>
        <TextField
          fullWidth
          label="City"
          onChange={(e) => changeAddress("city", e.target.value)}
          error={customer_error?.city_has_error}
          helperText={customer_error?.city_error ?? ""}
          value={customer.address.city}
          size="small" />
      </Grid>
      <Grid item xs={3} sx={{ paddingRight: 1 }}>
        <TextField
          fullWidth
          label="Province"
          disabled
          value="AB"
          size="small" />
      </Grid>
      <Grid item xs={3} sx={{ paddingRight: 1 }}>
        <TextField
          fullWidth
          label="Country"
          disabled
          value="CA"
          size="small" />
      </Grid>
      <Grid item xs={3} sx={{ paddingRight: 1 }}>
        <TextField
          fullWidth
          label="Postal Code"
          onChange={(e) => changeAddress("postal_code", e.target.value)}
          error={customer_error?.postal_code_has_error}
          helperText={customer_error?.postal_code_error}
          value={customer.address.postal_code}
          size="small" />
      </Grid>
      <Grid item xs={12} sx={{ marginBottom: customer_error.email_has_error || customer_error.phone_has_error ? 0 : 0.5 }}>
        <Typography variant="body2" sx={{ color: grey_color, paddingLeft: 0.5, paddingTop: 1 }}>
          Contact
        </Typography>
      </Grid>
      <Grid item xs={3} sx={{ paddingRight: 1 }}>
        <TextField
          fullWidth
          label="Phone (555-555-5555)"
          onChange={(e) => changeCustomer("phone", e.target.value)}
          error={customer_error?.phone_has_error}
          helperText={customer_error?.phone_error ?? ""}
          value={customer.phone}
          size="small" />
      </Grid>
      <Grid item xs={3} sx={{ paddingRight: 1 }}>
        <TextField
          fullWidth
          label="Email"
          onChange={(e) => changeCustomer("email", e.target.value)}
          error={customer_error?.email_has_error}
          helperText={customer_error?.email_error ?? ""}
          value={customer.email}
          size="small" />
      </Grid>
      <Grid item xs={6}></Grid>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="flex-end" sx={{ marginTop: 20 }}>
          {
            loading ?
              <CircularProgress /> :
              <Button variant="contained" endIcon={<ArrowForwardIosIcon />} onClick={handleSubmitBilling} >
                Submit
              </Button>
          }
        </Box>
      </Grid>
    </Grid>
  );
}
